import React, {
  useState,
  createContext,
  memo,
  useCallback,
  useEffect,
} from 'react';
import {
  extractProductInfo,
  getAllProducts,
  getProductFromId,
  publishProduct,
  updateProduct,
  deleteProduct,
  toggleProductStatus,
  incrementProductField,
  getProductFromShortCode,
} from '../utils/api';
import { toast } from 'react-toastify';
import { logClick, listMagicLinks } from '../utils/shopify';
import { useAuth } from '../hooks/useAuth';

export type ProductDraft = {
  docId: string;
  handle: string;
  title: string;
  id?: string;
  productURL: string;
  name: string;
  description: string;
  imageURL: string | null;
  image?: File | string | null;
  authors?: string[] | string | null;
  createdAt?: string;
  updatedAt?: string;
  isActive?: boolean;
  viewCount?: number;
  clickCount?: number;
  shortCode?: string | null;
  magicLinkId?: string | null;
};

interface ProductCtxType {
  allProducts: ProductDraft[];
  setAllProducts: React.Dispatch<React.SetStateAction<ProductDraft[]>>;
  productInfo: ProductDraft | null;
  setProductInfo: React.Dispatch<React.SetStateAction<ProductDraft | null>>;
  loadProducts: (userId: string, include_inactive: boolean) => Promise<void>;
  loadProductById: (
    userId: string,
    productId: string
  ) => Promise<ProductDraft | null>;
  loadProductByShortCode: (
    userId: string,
    shortCode: string
  ) => Promise<ProductDraft | null>;
  handleExtractProduct: (
    userId: string,
    link: string
  ) => Promise<ProductDraft | null>;
  handlePublishProduct: (userId: string) => Promise<ProductDraft | null>;
  handleUpdateProduct: (userId: string) => Promise<ProductDraft | null>;
  handleDeleteProduct: (userId: string, productId: string) => Promise<void>;
  toggleStatus: (userId: string, productId: string) => Promise<void>;
  incrementViewCount: (userId: string, productId: string) => Promise<void>;
  incrementClickCount: (userId: string, productId: string) => Promise<void>;
}

const asInt = (v: any, fallback = 0) =>
  typeof v === 'number'
    ? v
    : Number.isFinite(parseInt(v, 10))
    ? parseInt(v, 10)
    : fallback;

const unwrap = (res: any) =>
  res?.product ?? res?.data ?? res?.item ?? res?.result ?? res;

const wireToDraft = (raw: any): ProductDraft => {
  const p = unwrap(raw) ?? raw ?? {};
  // detect extract_url_info
  const looksLikeExtract =
    !!p?.suggested_product_data ||
    typeof p?.url === 'string' ||
    typeof p?.title === 'string' ||
    typeof p?.image === 'string';

  if (looksLikeExtract) {
    const s = p.suggested_product_data ?? {};
    const authors =
      Array.isArray(p?.authors) && p.authors.length
        ? p.authors.join(', ')
        : typeof p?.authors === 'string'
        ? p.authors
        : null;

    return {
      id: p.id ?? p.product_id ?? p._id,
      productURL: s.product_url ?? p.product_url ?? p.url ?? '',
      docId: p.docId ?? p._id ?? p.id,
      title: s.title ?? p.title ?? '',
      handle: s.handle ?? '',
      name: s.name ?? p.name ?? p.title ?? '',
      description: s.description ?? p.description ?? '',
      image: null,
      imageURL: s.image_url ?? '',
      authors,
      createdAt: p.createdAt,
      updatedAt: p.updatedAt,
      isActive:
        typeof p.is_active === 'boolean' ? p.is_active : p.isActive ?? true,
      viewCount: asInt(p.view_count ?? p.viewCount, 0),
      clickCount: asInt(p.click_count ?? p.clickCount, 0),
      shortCode: p.short_code ?? null,
      magicLinkId: p.magic_link_id ?? null,
    };
  }

  // default: ProductResponse-like object
  return {
    id: p.id ?? p.product_id ?? p._id,
    docId: p.docId ?? p._id ?? p.id,
    productURL: p.product_url ?? '',
    title: p.title ?? p.title ?? '',
    handle: p.handle ?? '',
    name: p.name ?? '',
    description: p.description ?? '',
    image: null,
    imageURL: p.image_url ?? '',
    authors: null,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
    isActive:
      typeof p.is_active === 'boolean' ? p.is_active : p.isActive ?? true,
    viewCount: asInt(p.view_count ?? p.viewCount, 0),
    clickCount: asInt(p.click_count ?? p.clickCount, 0),
    shortCode: p.short_code ?? null,
    magicLinkId: p.magic_link_id ?? null,
  };
};

const normalizeList = (res: any): ProductDraft[] => {
  if (Array.isArray(res)) return res.map(wireToDraft);
  if (Array.isArray(res?.products)) return res.products.map(wireToDraft);
  if (Array.isArray(res?.data)) return res.data.map(wireToDraft);
  if (Array.isArray(res?.items)) return res.items.map(wireToDraft);
  if (Array.isArray(res?.results)) return res.results.map(wireToDraft);
  return [];
};

export const ProductContext = createContext<ProductCtxType>({
  allProducts: [],
  setAllProducts: () => {
    return {} as any;
  },
  productInfo: null,
  setProductInfo: () => {
    return {} as any;
  },

  loadProducts: async () => {},
  loadProductById: async () => null,
  loadProductByShortCode: async () => null,
  handleExtractProduct: async () => null,
  handlePublishProduct: async () => null,
  handleUpdateProduct: async () => null,
  handleDeleteProduct: async () => {},
  toggleStatus: async () => {},
  incrementViewCount: async () => {},
  incrementClickCount: async () => {},
});

export const ProductContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [allProducts, setAllProducts] = useState<ProductDraft[]>([]);
  const [productInfo, setProductInfo] = useState<ProductDraft | null>(null);
  const { authUser } = useAuth();

  /** Load all products for a user */
  const loadProducts = useCallback(
    async (userId: string, include_inactive: boolean = true, token = '') => {
      try {
        const res = await getAllProducts(userId, include_inactive, token);
        setAllProducts(normalizeList(res) || []);
      } catch (err) {
        console.error('Error loading products', err);
        throw err;
      }
    },
    []
  );

  /** Load single product by ID */
  const loadProductById = useCallback(
    async (
      userId: string,
      productId: string,
      include_inactive: boolean = true,
      token = ''
    ) => {
      try {
        const res = await getProductFromId(
          userId,
          productId,
          include_inactive,
          token
        );
        if (res) {
          const draft = wireToDraft(res);
          if (draft) {
            setProductInfo(draft);
            return draft;
          }
        }
        return null;
      } catch (err) {
        console.error(`Error loading product ${productId}`, err);
        throw err;
      }
    },
    []
  );

  /** Extract product info from a link */
  const handleExtractProduct = useCallback(
    async (
      userId: string,
      link: string,
      token = ''
    ): Promise<ProductDraft | null> => {
      try {
        const res = await extractProductInfo(userId, link, token);
        if (res) {
          const draft = wireToDraft(res?.data ?? res);
          setProductInfo(draft);
          return draft;
        }
        setProductInfo({
          ...productInfo,
          productURL: link,
          name: '',
          description: '',
        });
        return null;
      } catch (err) {
        console.error('Error extracting product info', err);
        return null;
      }
    },
    []
  );

  /** Publish a new product */
  const handlePublishProduct = useCallback(
    async (userId: string, token = '') => {
      if (!productInfo) return null;

      try {
        const payload = productInfo;
        // Decide image payload:
        // - If File or data: send base64 in `image`, and DO NOT send imageURL
        // - If http/https URL: send sanitized imageURL and DO NOT send image

        if (productInfo.image instanceof File) payload.imageURL = '';
        // Build payload (omit conflicting fields)

        const published = await publishProduct(userId, payload, token);
        if (!published) return null;

        const created = wireToDraft(published);
        if (created) {
          setAllProducts((prev) => [...prev, created]);
        }

        setProductInfo(null);
        toast.success('Your product has been added!');
        return created;
      } catch (err) {
        // Prefer throwing so caller can handle
        throw err;
      }
    },
    [productInfo, setAllProducts, setProductInfo]
  );

  /** Update existing product */
  const handleUpdateProduct = useCallback(
    async (userId: string, token = ''): Promise<ProductDraft | null> => {
      if (!productInfo?.id) return null;

      try {
        // Baseline to compare against (from list)
        const baseline =
          allProducts.find((p) => p.id === productInfo.id) ?? null;

        const patch: any = {
          user_id: userId,
          product_id: productInfo.id,
        };

        // Add changed scalar fields only (name, description, is_active)
        if (!baseline || baseline.name !== productInfo.name) {
          patch.name = productInfo.name;
        }
        if (!baseline || baseline.description !== productInfo.description) {
          patch.description = productInfo.description;
        }
        if (!baseline || baseline.isActive !== productInfo.isActive) {
          patch.is_active = productInfo.isActive;
        }

        if (productInfo.image) {
          patch.image = productInfo.image;
        }

        // If nothing changed besides identifiers, skip the request
        const keysToSend = Object.keys(patch).filter(
          (k) => !['user_id', 'product_id'].includes(k)
        );
        if (keysToSend.length === 0) {
          return baseline;
        }

        // Send only the minimal patch
        const res = await updateProduct(userId, patch, token);
        if (!res) return null;

        const updated = wireToDraft(res);
        if (!updated?.id) return null;

        setAllProducts((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          const idx = list.findIndex((p) => p.id === updated.id);
          if (idx !== -1) list[idx] = updated;
          else list.push(updated);
          return list;
        });
        setProductInfo({ ...updated, image: null });

        toast.success('Your product has been updated!');
        return updated;
      } catch (err) {
        console.error('Error updating product', err);
        throw err;
      }
    },
    [productInfo, allProducts, setAllProducts, setProductInfo]
  );

  /** Delete a product */
  const handleDeleteProduct = useCallback(
    async (userId: string, productId: string, token = '') => {
      try {
        const res = await deleteProduct(userId, productId, token);
        if (res) {
          setAllProducts((prev) =>
            Array.isArray(prev) ? prev.filter((p) => p.id !== productId) : []
          );
          setProductInfo(null);
        }
      } catch (err) {
        console.error('Error deleting product', err);
      }
    },
    []
  );

  /** Toggle Active Status */
  const toggleStatus = useCallback(
    async (userId: string, productId: string) => {
      try {
        const res = await toggleProductStatus(userId, productId);
        if (res) {
          setAllProducts((prev) => {
            if (!Array.isArray(prev)) return prev as any;
            return prev.map((p) =>
              p.id === res.product_id
                ? { ...p, isActive: res.new_active_status ?? !p.isActive }
                : p
            );
          });
        }
      } catch (err) {
        console.error('Error toggling product status', err);
      }
    },
    [setAllProducts]
  );

  /** Increment View Count */
  const incrementViewCount = useCallback(
    async (userId: string, productId: string) => {
      try {
        const res = await incrementProductField(
          userId,
          productId,
          'view_count'
        );
        if (res) {
          setAllProducts((prev) => {
            if (!Array.isArray(prev)) return prev as any;
            return prev.map((p) =>
              p.id === res.product_id
                ? { ...p, viewCount: res.new_value ?? !p.viewCount }
                : p
            );
          });
        }
      } catch (err) {
        console.error('Error Incrementing view count', err);
      }
    },
    [setAllProducts]
  );

  /** Increment Click Count */
  const incrementClickCount = useCallback(
    async (userId: string, productId: string) => {
      try {
        // Optimistically update local state
        setAllProducts((prev) => {
          if (!Array.isArray(prev)) return prev as any;
          return prev.map((p) =>
            p.id === productId
              ? { ...p, clickCount: (p.clickCount || 0) + 1 }
              : p
          );
        });

        // Log to Shopify
        try {
          const magicLinkRes = await listMagicLinks({
            product_id: productId,
            limit: 1,
          });
          const magicLinkId = magicLinkRes?.items?.[0]?.magic_link_id;

          if (magicLinkId) {
            await logClick(magicLinkId, {
              user_id: authUser?.uid,
            });
            console.log('Shopify click logged for product:', productId);
          } else {
            console.log('No magic link found for Shopify logging:', productId);
          }
        } catch (shopifyErr) {
          console.error('Error logging to Shopify:', shopifyErr);
        }
      } catch (err) {
        console.error('Error in incrementClickCount', err);
      }
    },
    [setAllProducts, authUser?.uid]
  );

  /** Get Product From Short Code */
  const loadProductByShortCode = useCallback(
    async (userId: string, shortCode: string) => {
      try {
        const res = await getProductFromShortCode(userId, shortCode);
        if (res) {
          const draft = wireToDraft(res);
          if (draft) {
            setProductInfo(draft);
            return draft;
          }
        }
        return null;
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    []
  );

  return (
    <ProductContext.Provider
      value={{
        allProducts,
        setAllProducts,
        productInfo,
        setProductInfo,
        loadProducts,
        loadProductById,
        loadProductByShortCode,
        handleExtractProduct,
        handlePublishProduct,
        handleUpdateProduct,
        handleDeleteProduct,
        toggleStatus,
        incrementViewCount,
        incrementClickCount,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export default memo(ProductContextProvider);
